/**
 * Utility to convert markdown-formatted text to SSML (Speech Synthesis Markup Language)
 * for use with ElevenLabs Text-to-Speech API
 */

/**
 * Convert markdown text to SSML format
 * @param {string} text - The markdown-formatted text
 * @param {Object} options - Conversion options
 * @returns {string} - SSML-formatted text
 */
export function markdownToSSML(text, options = {}) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Default options
  const opts = {
    headerPause: '0.5s',
    listItemPause: '0.3s',
    paragraphPause: '0.4s',
    enableEmphasis: true,
    ...options
  };

  let ssmlText = text;

  // Remove code blocks and inline code (don't speak code)
  ssmlText = ssmlText.replace(/```[\s\S]*?```/g, ' [code block removed] ');
  ssmlText = ssmlText.replace(/`([^`]+)`/g, '$1');

  // Convert headers to text with pauses
  // H1: # Header
  ssmlText = ssmlText.replace(/^# (.+)$/gm, (match, p1) => {
    return `<break time="${opts.headerPause}"/>${p1}<break time="${opts.headerPause}"/>`;
  });

  // H2: ## Header
  ssmlText = ssmlText.replace(/^## (.+)$/gm, (match, p1) => {
    return `<break time="${opts.headerPause}"/>${p1}<break time="${opts.headerPause}"/>`;
  });

  // H3-H6: ###+ Header
  ssmlText = ssmlText.replace(/^#{3,6} (.+)$/gm, (match, p1) => {
    return `<break time="0.3s"/>${p1}<break time="0.3s"/>`;
  });

  // Convert bold text
  if (opts.enableEmphasis) {
    // **bold** or __bold__
    ssmlText = ssmlText.replace(/\*\*([^*]+)\*\*/g, '<emphasis level="strong">$1</emphasis>');
    ssmlText = ssmlText.replace(/__([^_]+)__/g, '<emphasis level="strong">$1</emphasis>');
  } else {
    // Just remove the markdown
    ssmlText = ssmlText.replace(/\*\*([^*]+)\*\*/g, '$1');
    ssmlText = ssmlText.replace(/__([^_]+)__/g, '$1');
  }

  // Convert italic text
  if (opts.enableEmphasis) {
    // *italic* or _italic_ (but not within words)
    // Using (^|[^\w]) instead of (?<!\w) for Safari < 16.4 compatibility
    ssmlText = ssmlText.replace(/(^|[^\w])\*([^*]+)\*(?!\w)/g, '$1<emphasis level="moderate">$2</emphasis>');
    ssmlText = ssmlText.replace(/(^|[^\w])_([^_]+)_(?!\w)/g, '$1<emphasis level="moderate">$2</emphasis>');
  } else {
    // Just remove the markdown
    ssmlText = ssmlText.replace(/(^|[^\w])\*([^*]+)\*(?!\w)/g, '$1$2');
    ssmlText = ssmlText.replace(/(^|[^\w])_([^_]+)_(?!\w)/g, '$1$2');
  }

  // Convert lists
  // Unordered lists: *, -, +
  ssmlText = ssmlText.replace(/^[\*\-\+] (.+)$/gm, (match, p1) => {
    return `<break time="${opts.listItemPause}"/>${p1}`;
  });

  // Ordered lists: 1. 2. etc
  ssmlText = ssmlText.replace(/^\d+\. (.+)$/gm, (match, p1) => {
    return `<break time="${opts.listItemPause}"/>${p1}`;
  });

  // Convert blockquotes
  ssmlText = ssmlText.replace(/^> (.+)$/gm, (match, p1) => {
    return `<break time="0.2s"/>${p1}<break time="0.2s"/>`;
  });

  // Convert horizontal rules to pauses
  ssmlText = ssmlText.replace(/^---+$/gm, '<break time="1s"/>');
  ssmlText = ssmlText.replace(/^\*\*\*+$/gm, '<break time="1s"/>');

  // Convert links to just the link text
  // [text](url)
  ssmlText = ssmlText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove image markdown
  // ![alt](url)
  ssmlText = ssmlText.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');

  // Convert multiple newlines to paragraph pauses
  ssmlText = ssmlText.replace(/\n\n+/g, `\n<break time="${opts.paragraphPause}"/>\n`);

  // Clean up any remaining markdown characters
  ssmlText = ssmlText.replace(/^[#\*\-_]+\s*/gm, '');

  // Remove multiple spaces
  ssmlText = ssmlText.replace(/  +/g, ' ');

  // Trim whitespace
  ssmlText = ssmlText.trim();

  // Wrap in speak tags
  if (ssmlText) {
    ssmlText = `<speak>${ssmlText}</speak>`;
  }

  return ssmlText;
}

/**
 * Check if text contains markdown formatting
 * @param {string} text - The text to check
 * @returns {boolean} - True if markdown is detected
 */
export function containsMarkdown(text) {
  if (!text || typeof text !== 'string') {
    return false;
  }

  // Common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+/m,                    // Headers
    /\*\*[^*]+\*\*/,                  // Bold
    /__[^_]+__/,                      // Bold alt
    /(^|[^\w])\*[^*]+\*(?!\w)/,      // Italic (Safari-compatible)
    /(^|[^\w])_[^_]+_(?!\w)/,        // Italic alt (Safari-compatible)
    /^[\*\-\+]\s+/m,                  // Unordered list
    /^\d+\.\s+/m,                     // Ordered list
    /\[[^\]]+\]\([^)]+\)/,           // Links
    /!\[[^\]]*\]\([^)]+\)/,          // Images
    /^>\s+/m,                         // Blockquotes
    /```[\s\S]*?```/,                 // Code blocks
    /`[^`]+`/,                        // Inline code
    /^---+$/m,                        // Horizontal rule
  ];

  return markdownPatterns.some(pattern => pattern.test(text));
}

/**
 * Strip markdown formatting from text (for fallback)
 * @param {string} text - The markdown text
 * @returns {string} - Plain text without markdown
 */
export function stripMarkdown(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let plainText = text;

  // Remove code blocks
  plainText = plainText.replace(/```[\s\S]*?```/g, '');
  
  // Remove inline code
  plainText = plainText.replace(/`([^`]+)`/g, '$1');

  // Remove headers
  plainText = plainText.replace(/^#{1,6}\s+/gm, '');

  // Remove bold
  plainText = plainText.replace(/\*\*([^*]+)\*\*/g, '$1');
  plainText = plainText.replace(/__([^_]+)__/g, '$1');

  // Remove italic (Safari-compatible patterns)
  plainText = plainText.replace(/(^|[^\w])\*([^*]+)\*(?!\w)/g, '$1$2');
  plainText = plainText.replace(/(^|[^\w])_([^_]+)_(?!\w)/g, '$1$2');

  // Remove list markers
  plainText = plainText.replace(/^[\*\-\+]\s+/gm, '');
  plainText = plainText.replace(/^\d+\.\s+/gm, '');

  // Remove blockquotes
  plainText = plainText.replace(/^>\s+/gm, '');

  // Convert links to text
  plainText = plainText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove images
  plainText = plainText.replace(/!\[([^\]]*)\]\([^)]+\)/g, '');

  // Remove horizontal rules
  plainText = plainText.replace(/^---+$/gm, '');
  plainText = plainText.replace(/^\*\*\*+$/gm, '');

  // Clean up multiple spaces and newlines
  plainText = plainText.replace(/  +/g, ' ');
  plainText = plainText.replace(/\n\n\n+/g, '\n\n');

  return plainText.trim();
}